import React from "react";
import { Checkbox } from "../../atoms/Checkbox";
import { WorkspaceInitialsBadge } from "../../atoms/WorkspaceInitialsBadge";
import { Row, Body, Name, Slug, CheckSlot } from "./TenantSelectRow.styles";
import type { TenantSelectRowProps } from "./TenantSelectRow.types";

/**
 * One selectable tenant in the tenant-selection step.
 *
 * Checkbox visuals, radio semantics — the design calls for checkboxes but only
 * one tenant may be chosen. `role="radio"` is what makes a screen reader
 * announce "1 of 4" rather than four independent on/off switches. The visual
 * `Checkbox` atom is `aria-hidden` so the control is announced exactly once.
 *
 * Stateless, per atomic-design rules: selection lives in the organism/page.
 *
 * The secondary line shows `role_name`: this branch's `MembershipRead` carries
 * the caller's role rather than a tenant slug, so the row surfaces the field
 * the tenant-context API actually returns.
 */
export const TenantSelectRow: React.FC<TenantSelectRowProps> = ({
  tenant,
  selected,
  onSelect,
  disabled = false,
  className,
}) => (
  <Row
    type="button"
    role="radio"
    aria-checked={selected}
    disabled={disabled}
    $selected={selected}
    className={className}
    onClick={() => onSelect(tenant.tenant_id)}
  >
    <WorkspaceInitialsBadge name={tenant.tenant_name} ariaLabel="" />
    <Body>
      <Name title={tenant.tenant_name}>{tenant.tenant_name}</Name>
      <Slug>{tenant.role_name}</Slug>
    </Body>
    <CheckSlot aria-hidden="true">
      <Checkbox checked={selected} disabled={disabled} />
    </CheckSlot>
  </Row>
);

export default TenantSelectRow;
